import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Filter, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  useExecutions,
  useDeleteExecution,
} from '@/features/executions/api/useExecutions'
import { useTestPlans } from '@/features/test-plans/api/useTestPlans'
import { useBuilds } from '@/features/test-plans/api/useBuilds'
import {
  ExecutionStatus,
  getExecutionStatusLabel,
  getExecutionStatusColor,
} from '@/types/execution.types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString()
}

const ExecutionsPage = () => {
  const [selectedTestPlanId, setSelectedTestPlanId] = useState<string>('all')
  const [selectedBuildId, setSelectedBuildId] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteExecutionId, setDeleteExecutionId] = useState<number | null>(null)

  // GET /api/testplans, GET /api/builds
  const { data: testPlans } = useTestPlans()
  const { data: builds } = useBuilds(
    selectedTestPlanId && selectedTestPlanId !== 'all'
      ? { testplan_id: parseInt(selectedTestPlanId, 10) }
      : undefined
  )

  // Build filter params for GET /api/executions
  const filterParams = {
    ...(selectedTestPlanId !== 'all'
      ? { testplan_id: parseInt(selectedTestPlanId, 10) }
      : {}),
    ...(selectedBuildId !== 'all'
      ? { build_id: parseInt(selectedBuildId, 10) }
      : {}),
    ...(selectedStatus !== 'all' ? { status: selectedStatus } : {}),
  }

  const hasFilters = Object.keys(filterParams).length > 0
  const { data: executions, isLoading } = useExecutions(
    hasFilters ? filterParams : {}
  )
  const deleteMutation = useDeleteExecution()

  // Normalize response shapes
  const testPlanList = Array.isArray(testPlans)
    ? testPlans
    : (testPlans as any)?.testplans || []
  const buildList = Array.isArray(builds)
    ? builds
    : (builds as any)?.builds || []
  const executionList = Array.isArray(executions)
    ? executions
    : (executions as any)?.executions || []

  // Client-side search
  const filteredExecutions = executionList.filter((exec: any) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      exec.testcase_name?.toLowerCase().includes(q) ||
      exec.build_name?.toLowerCase().includes(q) ||
      exec.testplan_name?.toLowerCase().includes(q) ||
      exec.tester_name?.toLowerCase().includes(q) ||
      exec.notes?.toLowerCase().includes(q)
    )
  })

  const activeFiltersCount = [
    selectedTestPlanId !== 'all',
    selectedBuildId !== 'all',
    selectedStatus !== 'all',
    searchQuery.length > 0,
  ].filter(Boolean).length

  const handleClearFilters = () => {
    setSelectedTestPlanId('all')
    setSelectedBuildId('all')
    setSelectedStatus('all')
    setSearchQuery('')
  }

  // DELETE /api/executions/{id}
  const handleDeleteExecution = async () => {
    if (deleteExecutionId !== null) {
      try {
        await deleteMutation.mutateAsync(deleteExecutionId)
        toast.success('Execution deleted')
      } catch {
        toast.error('Failed to delete execution')
      }
      setDeleteExecutionId(null)
    }
  }

  // Reset build filter when test plan changes
  const handleTestPlanChange = (value: string) => {
    setSelectedTestPlanId(value)
    setSelectedBuildId('all')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Test Executions</h1>
          <p className="text-muted-foreground mt-1">
            View and manage test execution results
          </p>
        </div>
        <Button asChild>
          <Link to="/executions/test-executions/new">
            <Plus className="mr-2 h-4 w-4" />
            Record Execution
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
              {activeFiltersCount > 0 && (
                <Badge variant="secondary">{activeFiltersCount} active</Badge>
              )}
            </CardTitle>
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                <X className="mr-2 h-4 w-4" />
                Clear All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Test Plan Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">Test Plan</label>
              <Select value={selectedTestPlanId} onValueChange={handleTestPlanChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All test plans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Test Plans</SelectItem>
                  {testPlanList.map((tp: any) => (
                    <SelectItem key={tp.id} value={tp.id.toString()}>
                      {tp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Build Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">Build</label>
              <Select
                value={selectedBuildId}
                onValueChange={setSelectedBuildId}
                disabled={selectedTestPlanId === 'all'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All builds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Builds</SelectItem>
                  {buildList.map((b: any) => (
                    <SelectItem key={b.id} value={b.id.toString()}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">Status</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value={ExecutionStatus.Passed}>Passed</SelectItem>
                  <SelectItem value={ExecutionStatus.Failed}>Failed</SelectItem>
                  <SelectItem value={ExecutionStatus.Blocked}>Blocked</SelectItem>
                  <SelectItem value={ExecutionStatus.NotRun}>Not Run</SelectItem>
                  <SelectItem value={ExecutionStatus.Pending}>Pending</SelectItem>
                  <SelectItem value={ExecutionStatus.Running}>Running</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div>
              <label className="text-sm font-medium mb-1 block">Search</label>
              <Input
                placeholder="Search by test case, build, plan..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle>
            Executions ({filteredExecutions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading executions...
            </div>
          ) : filteredExecutions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No executions found.{' '}
              <Link
                to="/executions/test-executions/new"
                className="text-primary hover:underline"
              >
                Record your first execution
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">ID</TableHead>
                  <TableHead>Test Case</TableHead>
                  <TableHead>Test Plan</TableHead>
                  <TableHead>Build</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead>Tester</TableHead>
                  <TableHead>Executed</TableHead>
                  <TableHead className="w-[80px]">Duration</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExecutions.map((exec: any) => (
                  <TableRow key={exec.id}>
                    <TableCell className="font-mono">{exec.id}</TableCell>
                    <TableCell>
                      <Link
                        to={`/executions/test-executions/${exec.id}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {exec.testcase_name || `TC-${exec.tcversion_id}`}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {exec.testplan_name || `Plan #${exec.testplan_id}`}
                    </TableCell>
                    <TableCell>
                      {exec.build_name || `Build #${exec.build_id}`}
                    </TableCell>
                    <TableCell>
                      <Badge className={getExecutionStatusColor(exec.status)}>
                        {getExecutionStatusLabel(exec.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {exec.tester_name || exec.tester_id || '-'}
                    </TableCell>
                    <TableCell>
                      {exec.execution_ts ? formatDate(exec.execution_ts) : '-'}
                    </TableCell>
                    <TableCell>
                      {exec.execution_duration
                        ? `${exec.execution_duration}s`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteExecutionId(exec.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog
        open={deleteExecutionId !== null}
        onOpenChange={() => setDeleteExecutionId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Execution</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this execution record and all associated
              step results. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteExecution}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default ExecutionsPage
